import React, { useState, useEffect, useRef } from 'react'
import { FormattedMessage } from 'react-intl'
import RadioCheck from '../../../components/MessageManager/RadioCheck'
import { SignageZonePanel, AddSignageButton } from './zonePanelsConfig'
import commonFunctions from '../../../utils/commonFunctions'
import { SIGNAGE_MEDIA } from '../../../utils/constants'
import API from '../../../utils/API'
import { getWeatherWidget, getCalendarWidget, getDigitalClockWidget, getAnalogClockWidget, updateAnalogClock } from './PreviewContent/widget';
import { UpdatePlaylistContent } from './PreviewContent/UpdatePlaylistContent';

const SignageMainConfigPanel = ({
  signageName,
  setSignageName,
  signageNameValid,
  zoneType,
  setZoneType,
  zoneOptions,
  zonePanelsConfig,
  validated,
  zone,
  setZone,
  zoneData,
  setPlayListLengthValid,
  intl,
  markUnsavedChanges
}) => {
  const panelRefs = useRef({});

  const getPlaylistContent = async (id) => {
    const playlist = await API.getPlaylistItems(id);
    return playlist;
  }

  useEffect(() => {
    Object.keys(panelRefs.current).forEach((key) => {
      if (!zonePanelsConfig[zoneType]?.some((colConfig) => colConfig.panels.some((panel) => panel.zone === key))) {
        delete panelRefs.current[key];
      }
    });

    const adjustPaddingBottom = () => {
      let signageMainPanel = document.querySelector('.signage-main-panel');
      let col9Div = signageMainPanel?.querySelector('.col-9');
      let panelContainer = signageMainPanel?.querySelector('.col-9 .panel-container');
      let col3Div = signageMainPanel?.querySelector('.col-3');
      if (zoneType === "10") {
        signageMainPanel.style.paddingBottom = `20px`;
        signageMainPanel.querySelector('.col-12').style.maxHeight = '100%';
        return;
      };
      let col9Height = col9Div.offsetWidth / (16 / 9);
      if (signageMainPanel && col9Div) {
        let width = signageMainPanel.offsetWidth;
        let idealHeight = width / (16 / 9);
        panelContainer.style.maxHeight = `${col9Height}px`;
        let paddingBottom = Math.max(0, idealHeight - col9Height);

        if (zoneType === "40" || zoneType === "32") {
          paddingBottom = paddingBottom - 100;
        }
        signageMainPanel.style.paddingBottom = `${paddingBottom}px`;
      }
      if (col3Div) {
        col3Div.style.maxHeight = `${col9Height}px`;
      }
    };

    adjustPaddingBottom();
    window.addEventListener('resize', adjustPaddingBottom);
    return () => {
      window.removeEventListener('resize', adjustPaddingBottom);
    };
  }, [zoneType]);

  useEffect(() => {
    const container = panelRefs.current[SIGNAGE_MEDIA.CREATE_SIGNAGE.TICKER];
    if (!container || !zoneData[SIGNAGE_MEDIA.CREATE_SIGNAGE.TICKER]) return;

    const content = zoneData[SIGNAGE_MEDIA.CREATE_SIGNAGE.TICKER]?.content;
    container.innerHTML = `<div class="text-left pl-3" style="display: inline-block; border: 1px solid rgba(173, 189, 205, 1); border-radius: 12px; padding: 10px;margin: 16px 20px; max-width: calc(100% - 40px);overflow: hidden;white-space: nowrap;text-overflow: ellipsis;">
      ${content?.message || ""}
    </div>`;
  }, [zoneData[SIGNAGE_MEDIA.CREATE_SIGNAGE.TICKER], zoneType]);

  useEffect(() => {
    const widgetTimers = {};
    const resizeHandlers = {};

    const handleWidgetUpdate = async (zone) => {
      const container = panelRefs.current[zone];
      if (!container || !zoneData[zone]) return;

      const content = zoneData[zone]?.content;
      const subcategory = content.subcategory;

      if (resizeHandlers[zone]) {
        window.removeEventListener('resize', resizeHandlers[zone]);
        delete resizeHandlers[zone];
      }

      switch (subcategory) {
        case SIGNAGE_MEDIA.MEDIA_SUB_CATEGORY.WEATHER:
          const weatherResizeHandler = async () => {
            if (!container || !content || !content.paramters) return;

            container.innerHTML = await getWeatherWidget(content.paramters);
            const containerHeight = container.offsetHeight;
            if (containerHeight <= 0) return;

            const weatherMainInfoDiv = container.querySelector('.weather-main-info');
            if (!weatherMainInfoDiv) return;

            if (containerHeight < weatherMainInfoDiv.offsetHeight) {
              let scale = containerHeight / weatherMainInfoDiv.offsetHeight;
              if (scale < 1) {
                weatherMainInfoDiv.style.transform = `scale(${scale})`;
              }
            }
          }
          weatherResizeHandler();
          resizeHandlers[zone] = weatherResizeHandler;
          window.addEventListener('resize', resizeHandlers[zone]);
          break;
        case SIGNAGE_MEDIA.MEDIA_SUB_CATEGORY.CLOCK:
          container.innerHTML = '';
          if (content.paramters?.type === SIGNAGE_MEDIA.MEDIA_TYPE.TYPE_ANALOG) {
            container.style.background = "linear-gradient(0deg, #171719, #171719),linear-gradient(180deg, rgba(255, 255, 255, 0.2) 0%, rgba(24, 24, 26, 0.2) 100%, rgba(24, 24, 26, 0.2) 100%)";
            container.style.alignContent = "center";

            await getAnalogClockWidget(subcategory + zone, container, content.paramters);
            widgetTimers[zone] = setInterval(() => {
              updateAnalogClock(subcategory + zone, content.paramters);
            }, 1000);
          } else {
            container.style.background = undefined;
            container.style.alignContent = undefined;

            const digitalClockResizeHandler = async () => {
              container.innerHTML = '';
              await getDigitalClockWidget(subcategory + zone, container, content.paramters);
              const digitalClockMainInfoDiv = container.querySelector('.clock-main-info');
              if (container.offsetWidth < 258) {
                let scale = container.offsetWidth / 258;
                if (digitalClockMainInfoDiv && scale < 1) {
                  digitalClockMainInfoDiv.style.transform = `scale(${scale})`;
                  digitalClockMainInfoDiv.style.transformOrigin = 'center left';
                }
              }
            };
            digitalClockResizeHandler();
            resizeHandlers[zone] = digitalClockResizeHandler;
            window.addEventListener('resize', digitalClockResizeHandler);
          }
          break;
        case SIGNAGE_MEDIA.MEDIA_SUB_CATEGORY.CALENDAR:
          const calendarResizeHandler = async () => {
            container.innerHTML = '';
            await getCalendarWidget(subcategory + zone, container, content.paramters);
            const calendarMainInfoDiv = container.querySelector('.calendar-main-info');
            if (container.offsetHeight < calendarMainInfoDiv.offsetHeight) {
              let scale = container.offsetHeight / calendarMainInfoDiv.offsetHeight;

              if (calendarMainInfoDiv && scale < 1) {
                calendarMainInfoDiv.style.transform = `scale(${scale})`;
              }
            }
          };
          calendarResizeHandler();
          resizeHandlers[zone] = calendarResizeHandler;
          window.addEventListener('resize', resizeHandlers[zone]);
          break;
        default:
          container.innerHTML = `<h4>${content.name}</h4><p>${content.description || "No description available"}</p>`;
          break;
      }
    };

    handleWidgetUpdate(SIGNAGE_MEDIA.CREATE_SIGNAGE.WIDGET1);
    handleWidgetUpdate(SIGNAGE_MEDIA.CREATE_SIGNAGE.WIDGET2);
    return () => {
      Object.values(widgetTimers).forEach(clearInterval);
      Object.keys(resizeHandlers).forEach((zone) => {
        window.removeEventListener('resize', resizeHandlers[zone]);
      });
    };
  }, [zoneData[SIGNAGE_MEDIA.CREATE_SIGNAGE.WIDGET1], zoneData[SIGNAGE_MEDIA.CREATE_SIGNAGE.WIDGET2], zoneType]);

  useEffect(() => {
    const container = panelRefs.current[SIGNAGE_MEDIA.CREATE_SIGNAGE.PLAYLIST];
    if (!container || !zoneData[SIGNAGE_MEDIA.CREATE_SIGNAGE.PLAYLIST]) return;

    const content = zoneData[SIGNAGE_MEDIA.CREATE_SIGNAGE.PLAYLIST]?.content;
    let items = content;
    let isLoop = zoneData[SIGNAGE_MEDIA.CREATE_SIGNAGE.PLAYLIST].isLoop;

    const updatePlaylist = async () => {
      if (!items && zoneData[SIGNAGE_MEDIA.CREATE_SIGNAGE.PLAYLIST].playlistId) {
        const response = await getPlaylistContent(zoneData[SIGNAGE_MEDIA.CREATE_SIGNAGE.PLAYLIST].playlistId);
        items = response.data.items;
        isLoop = response.data.isLoop;
      }
      items = items || [];
      setPlayListLengthValid(items.length);
      await UpdatePlaylistContent(items, container, isLoop);
    };

    updatePlaylist();
  }, [zoneData[SIGNAGE_MEDIA.CREATE_SIGNAGE.PLAYLIST], zoneType]);

  return (
    <>
      <div className="mb-3">
        <label className="font-weight-600 font-size-14 mb-2" htmlFor="signageNameInput">
          <FormattedMessage id="SIGNAGE_NAME" defaultMessage="Signage Name" />:
        </label>
        <input
          id="signageNameInput"
          className={`form-control h-54 bg-baby-blue ${validated && (!signageNameValid ? 'is-valid' : 'is-invalid')}`}
          value={signageName}
          onChange={e => { setSignageName(e.target.value); markUnsavedChanges && markUnsavedChanges(); }}
          placeholder={intl.formatMessage({ id: "TYPE_HERE" })}
        />
        {commonFunctions.InvalidFeedbackString(signageNameValid, "SIGNAGE_NAME")}
      </div>
      <div className="mb-3">
        <label className="font-weight-600 font-size-14 mb-2">
          <FormattedMessage id="SIGNAGE_ZONE" defaultMessage="Signage Zone" />:
        </label>
        <RadioCheck
          options={zoneOptions}
          name="SIGNAGE_ZONE"
          selectedOption={zoneType}
          onChange={e => { setZoneType(e.target.value); markUnsavedChanges && markUnsavedChanges(); }}
        />
      </div>
      <div
        className="signage-main-panel border rounded "
        style={{
          padding: '20px 16px',
          display: 'flex',
          gap: '20px',
          flexWrap: 'wrap',
          height: '100%',
        }}
      >
        {zonePanelsConfig[zoneType] ? (
          <>
            {zonePanelsConfig[zoneType].map((colConfig, colIdx) => (
              <div
                key={colIdx}
                className={`col-${colConfig.col}${colConfig.className ? ' ' + colConfig.className : ''}`}
                style={
                  colConfig.style
                }
              >
                {colConfig.panels.map((panel, idx) => (
                  <div
                    key={idx}
                    className={`panel-container ${panel.panelClassName}`}
                    style={{ ...panel.panelStyle, alignItems: 'stretch', display: 'flex', flexDirection: 'column' }}
                  >
                    {!zoneData || (zoneData && !zoneData[panel.zone]) ? (
                      <SignageZonePanel style={{ background: "#F7FAFD" }}>
                        {panel.title && <div className="text-center fw-bold mb-2">{panel.title}</div>}
                        <AddSignageButton
                          label={panel.button}
                          {...panel.buttonProps}
                          onClick={() => setZone(panel.zone)}
                        />
                      </SignageZonePanel>
                    ) : (
                      <>
                        <div id={'signage-content-' + panel.zone}
                          ref={(el) => (panelRefs.current[panel.zone] = el)}
                          className="w-100 h-100 text-left" style={{
                            ...(panel.zone === SIGNAGE_MEDIA.CREATE_SIGNAGE.TICKER && {
                              border: "1px solid rgba(173, 189, 205, 1)",
                            }),
                            backgroundColor: "#F7FAFD",
                            borderRadius: "12px",
                            overflow: "hidden",
                          }}
                        />
                        <div className="overlay position-absolute top-0 start-0 w-100 h-100 d-flex justify-content-center align-items-center">
                          <button className="btn btn-custom" {...panel.buttonProps} onClick={() => setZone(panel.zone)}>
                            {panel.replaceButton}
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                ))}
              </div>
            ))}
          </>
        ) : null}
      </div>
    </>
  )
};

export default SignageMainConfigPanel;
